import { useEffect, useState } from 'react';
import { Button, Card, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

const PROVIDERS = [
  { value: 'browser', label: 'Browser voice (free, built-in)' },
  { value: 'google', label: 'Google Cloud Text-to-Speech' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'sarvam', label: 'Sarvam AI (text-to-speech)' },
];

// Speech-to-text is picked independently of the TTS voice above, so a company
// can e.g. speak with ElevenLabs and transcribe with Whisper. 'browser' means
// no server transcription — dictation falls back to the device Web Speech API.
const STT_PROVIDERS = [
  { value: 'browser', label: 'Browser / device only (free, built-in)' },
  { value: 'sarvam', label: 'Sarvam AI' },
  { value: 'openai', label: 'OpenAI Whisper' },
  { value: 'elevenlabs', label: 'ElevenLabs Scribe' },
];

const errorMessage = (e) => (e.response && e.response.data && e.response.data.error) || e.message;

/** Load/save state for the form — split out of the page component so the JSX below
 *  stays under this codebase's max-lines-per-function guideline. */
function useVoiceSettingsForm() {
  const { fetchApi } = useAppApi();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchApi('voiceSettingsStatus')
      .then((d) => { setStatus(d); setForm(d); })
      .catch((e) => setErr(errorMessage(e)));
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const applyPatch = async (patch, doneMessage) => {
    setSaving(true);
    setMessage('');
    setErr('');
    try {
      const d = await fetchApi('voiceSettingsSave', patch);
      setStatus(d);
      setForm(d);
      setMessage(doneMessage);
      return d;
    } catch (e) {
      setErr(errorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { status, form, setField, saving, message, err, applyPatch };
}

/** Masked, write-only key input — same shape as AgentDetail.jsx's ApiKeyControl (per-
 *  agent provider key override): never shows the actual key, only whether one is set,
 *  and a blank submit leaves it untouched (the "clear" affordance is a separate button,
 *  not "submit blank", since this form saves several fields — voice/tone — at once and
 *  a stray blank key field must not silently wipe an already-configured key). */
function KeyField({ label, hasKey, value, onChange, onClear, disabled }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label}</label>
      <div className="flex gap-2">
        <input
          type="password"
          autoComplete="off"
          disabled={disabled}
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] flex-1 disabled:opacity-60"
          placeholder={hasKey ? 'Configured — enter a new value to replace' : 'Enter API key'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {hasKey && (
          <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={onClear}>Clear</Button>
        )}
      </div>
      <p className="text-xs text-secondary-500 mt-1">{hasKey ? 'A key is configured.' : 'No key configured yet.'}</p>
    </div>
  );
}

function ToneSlider({ label, value, min, max, step, onChange, disabled, hint }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label} <span className="text-secondary-500">({value})</span></label>
      <input
        type="range" min={min} max={max} step={step} value={value} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full disabled:opacity-60"
      />
      {hint && <p className="text-xs text-secondary-500 mt-1">{hint}</p>}
    </div>
  );
}

/** Google/ElevenLabs-only voice-pick inputs — hidden for the free browser voice. */
function ProviderVoiceFields({ form, setField }) {
  if (form.activeProvider === 'google') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <input
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
          placeholder="Voice name (e.g. en-US-Neural2-F)"
          value={form.googleVoiceName || ''}
          onChange={(e) => setField('googleVoiceName', e.target.value)}
        />
        <input
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
          placeholder="Language code (e.g. en-US)"
          value={form.googleLanguageCode || ''}
          onChange={(e) => setField('googleLanguageCode', e.target.value)}
        />
      </div>
    );
  }
  if (form.activeProvider === 'elevenlabs') {
    return (
      <input
        className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
        placeholder="Voice ID (from your ElevenLabs account)"
        value={form.elevenLabsVoiceId || ''}
        onChange={(e) => setField('elevenLabsVoiceId', e.target.value)}
      />
    );
  }
  if (form.activeProvider === 'sarvam') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <input
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
          placeholder="Speaker voice (e.g. ishita)"
          value={form.sarvamVoice || ''}
          onChange={(e) => setField('sarvamVoice', e.target.value)}
        />
        <input
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
          placeholder="TTS model (e.g. bulbul:v3)"
          value={form.sarvamModel || ''}
          onChange={(e) => setField('sarvamModel', e.target.value)}
        />
        <input
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
          placeholder="Language code (e.g. en-IN)"
          value={form.sarvamLanguageCode || ''}
          onChange={(e) => setField('sarvamLanguageCode', e.target.value)}
        />
      </div>
    );
  }
  return null;
}

/** Per-STT-provider model inputs — shown under the independent STT picker below. */
function SttModelFields({ form, setField }) {
  if (form.sttProvider === 'sarvam') {
    return (
      <input
        className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
        placeholder="STT model (e.g. saaras:v4)"
        value={form.sarvamSttModel || ''}
        onChange={(e) => setField('sarvamSttModel', e.target.value)}
      />
    );
  }
  if (form.sttProvider === 'openai') {
    return (
      <input
        className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
        placeholder="STT model (e.g. gpt-4o-mini-transcribe)"
        value={form.openaiSttModel || ''}
        onChange={(e) => setField('openaiSttModel', e.target.value)}
      />
    );
  }
  if (form.sttProvider === 'elevenlabs') {
    return (
      <input
        className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
        placeholder="STT model (e.g. scribe_v1)"
        value={form.elevenLabsSttModel || ''}
        onChange={(e) => setField('elevenLabsSttModel', e.target.value)}
      />
    );
  }
  return null;
}

/** rate/pitch for browser+Google, stability/style for ElevenLabs — see services/tts/*.js
 *  for why those don't overlap (ElevenLabs has no direct rate/pitch knob). */
function ToneControls({ form, setField, saving }) {
  if (form.activeProvider === 'elevenlabs') {
    return (
      <>
        <ToneSlider
          label="Stability" value={form.stability} min={0} max={1} step={0.05}
          onChange={(v) => setField('stability', v)} disabled={saving}
          hint="Lower = more expressive/varied, higher = more consistent/monotone."
        />
        <ToneSlider
          label="Style exaggeration" value={form.style} min={0} max={1} step={0.05}
          onChange={(v) => setField('style', v)} disabled={saving}
          hint="Higher = more exaggerated delivery. ElevenLabs-only."
        />
      </>
    );
  }
  // Sarvam and the browser/Google voices share the generic rate/pitch pair —
  // Sarvam exposes no stability/style knobs.
  return (
    <>
      <ToneSlider
        label="Speaking rate" value={form.rate} min={0.5} max={2.0} step={0.05}
        onChange={(v) => setField('rate', v)} disabled={saving}
        hint="1.0 = normal speed. Applies to the browser voice and Google Cloud TTS."
      />
      <ToneSlider
        label="Pitch" value={form.pitch} min={-1} max={1} step={0.05}
        onChange={(v) => setField('pitch', v)} disabled={saving}
        hint="0 = neutral. Negative = lower/deeper, positive = higher."
      />
    </>
  );
}

/**
 * Admin-only voice settings for the chatbox character (voice.configure.org) — provider
 * choice, write-only API keys for Google/ElevenLabs, voice picks, and tone. GET is gated
 * more broadly (voice.view.org) so any org member can see current status, but the whole
 * form below is read-only without `configure`.
 */
export default function VoiceSettingsPage() {
  const { status, form, setField, saving, message, err, applyPatch } = useVoiceSettingsForm();
  const [googleKeyInput, setGoogleKeyInput] = useState('');
  const [elevenLabsKeyInput, setElevenLabsKeyInput] = useState('');
  const [sarvamKeyInput, setSarvamKeyInput] = useState('');

  const save = () => {
    const patch = {
      activeProvider: form.activeProvider,
      sttProvider: form.sttProvider || 'browser',
      googleVoiceName: form.googleVoiceName,
      googleLanguageCode: form.googleLanguageCode,
      elevenLabsVoiceId: form.elevenLabsVoiceId,
      sarvamVoice: form.sarvamVoice,
      sarvamModel: form.sarvamModel,
      sarvamLanguageCode: form.sarvamLanguageCode,
      sarvamSttModel: form.sarvamSttModel,
      openaiSttModel: form.openaiSttModel,
      elevenLabsSttModel: form.elevenLabsSttModel,
      rate: form.rate, pitch: form.pitch, stability: form.stability, style: form.style,
    };
    if (googleKeyInput.trim()) patch.googleApiKey = googleKeyInput.trim();
    if (elevenLabsKeyInput.trim()) patch.elevenLabsApiKey = elevenLabsKeyInput.trim();
    if (sarvamKeyInput.trim()) patch.sarvamApiKey = sarvamKeyInput.trim();
    applyPatch(patch, 'Saved.').then((d) => { if (d) { setGoogleKeyInput(''); setElevenLabsKeyInput(''); setSarvamKeyInput(''); } });
  };

  if (err && !status) return <PageShell><Alert variant="error" title="Could not load">{err}</Alert></PageShell>;
  if (!form) return null;

  return (
    <PageShell width="narrow" className="space-y-3 max-w-2xl">
      <PageHeader
        title="Chatbox Voice"
        subtitle={(
          <>
            Choose what the chatbox character speaks with, and how expressive it sounds. Falls back to the free
            browser voice automatically whenever no key is configured for the selected provider.
          </>
        )}
      />

      <Can module="voice" action="configure" fallback={<Alert variant="warning" title="Read-only">You can view these settings but not change them.</Alert>}>
        <Card className="p-3 space-y-4">
          <div>
            <label className="block text-sm mb-1">Text-to-speech provider</label>
            <select
              className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
              value={form.activeProvider}
              onChange={(e) => setField('activeProvider', e.target.value)}
            >
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <ProviderVoiceFields form={form} setField={setField} />

          <div>
            <label className="block text-sm mb-1">Speech-to-text provider</label>
            <select
              className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
              value={form.sttProvider || 'browser'}
              onChange={(e) => setField('sttProvider', e.target.value)}
            >
              {STT_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <p className="text-xs text-secondary-500 mt-1">
              Independent of the voice above. Used by dictation where the browser has no
              built-in speech recognition, and by the API transcription endpoint.
            </p>
          </div>

          <SttModelFields form={form} setField={setField} />

          <KeyField
            label="Google Cloud TTS API key" hasKey={status.hasGoogleKey}
            value={googleKeyInput} onChange={setGoogleKeyInput}
            onClear={() => applyPatch({ googleApiKey: '' }, 'Cleared.')} disabled={saving}
          />
          <KeyField
            label="ElevenLabs API key" hasKey={status.hasElevenLabsKey}
            value={elevenLabsKeyInput} onChange={setElevenLabsKeyInput}
            onClear={() => applyPatch({ elevenLabsApiKey: '' }, 'Cleared.')} disabled={saving}
          />
          <KeyField
            label="Sarvam API subscription key" hasKey={status.hasSarvamKey}
            value={sarvamKeyInput} onChange={setSarvamKeyInput}
            onClear={() => applyPatch({ sarvamApiKey: '' }, 'Cleared.')} disabled={saving}
          />
          <p className="text-xs text-secondary-500">
            OpenAI transcription uses the company&apos;s OpenAI AI-model connection
            {status.hasOpenAiKey ? ' (configured).' : ' (not configured — add one under Connections, or set OPENAI_API_KEY).'}
          </p>

          <div className="border-t border-[var(--app-border)] pt-4 space-y-3">
            <h2 className="text-sm font-semibold">Tone</h2>
            <ToneControls form={form} setField={setField} saving={saving} />
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            {message && <span className="text-sm text-emerald-600">{message}</span>}
          </div>
        </Card>
      </Can>

      {err && <Alert variant="error" title="Error">{err}</Alert>}
    </PageShell>
  );
}
